// Service for handling user location detection
export interface LocationData {
  latitude: number;
  longitude: number;
  city?: string;
  country?: string;
}

// Calculate distance between two coordinates
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + 
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Format distance for display
export const formatDistance = (distance: number): string => {
  if (distance < 1) {
    return `${Math.round(distance * 1000)} m`;
  } else if (distance < 10) {
    return `${distance.toFixed(1)} km`;
  } else {
    return `${Math.round(distance)} km`;
  }
};

// Get user location with fallback to Paris if denied or failed
export const getUserLocation = async (): Promise<LocationData> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.log('Geolocation not supported, using Paris as default');
      resolve({
        latitude: 48.8566,
        longitude: 2.3522,
        city: 'Paris',
        country: 'France'
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const location: LocationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };

        // Try to get city/country from reverse geocoding (simplified)
        try {
          // For now, we'll just use the coordinates
          // In a real app, you'd use a reverse geocoding service
          resolve(location);
        } catch (error) {
          resolve(location);
        }
      },
      (error) => {
        console.warn('Geolocation denied or failed, using Paris as default:', error);
        // Fallback to Paris coordinates
        resolve({
          latitude: 48.8566,
          longitude: 2.3522,
          city: 'Paris',
          country: 'France'
        });
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 600000 // 10 minutes cache
      }
    );
  });
};

// Get city name based on location (simplified version)
export const getCityFromLocation = (location: LocationData): string => {
  if (location.city) {
    return location.city;
  }
  
  // For now, return a default based on coordinates
  // In a real app, you'd use reverse geocoding
  return 'Votre ville';
};

// Find the city with the most places within radius
export const findNearestCityWithPlaces = (podcasts: any[], userLocation: LocationData, radius: number = 100) => {
  const cityCounts: { [city: string]: { count: number; distance: number } } = {};
  
  podcasts.forEach(podcast => {
    if (podcast.Latitude && podcast.Longitude && podcast.City) {
      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        podcast.Latitude,
        podcast.Longitude
      );
      
      if (distance <= radius) {
        const cityName = podcast.City;
        if (!cityCounts[cityName]) {
          cityCounts[cityName] = { count: 0, distance: distance };
        }
        cityCounts[cityName].count++;
        cityCounts[cityName].distance = Math.min(cityCounts[cityName].distance, distance);
      }
    }
  });
  
  if (Object.keys(cityCounts).length === 0) {
    return 'Paris'; // Fallback
  }
  
  // Find city with most places, then by closest distance
  const bestCity = Object.entries(cityCounts)
    .sort((a, b) => {
      if (b[1].count !== a[1].count) {
        return b[1].count - a[1].count; // Most places first
      }
      return a[1].distance - b[1].distance; // Then closest
    })[0];
    
  return bestCity[0];
};